/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Form, Grid, Tooltip } from '@mairistem/composants';

import { Section } from '@/components/Common';
import {
  OPTIONS_PUPILLE,
  OPTIONS_SITUATION_MATRIMONIALE,
} from '@/constants/optionsSituationFamilialeSection';

interface Props {
  nombreEnfants: number;
  nombreFreresSoeurs: number;
  pupille: string;
  situationFamiliale: string;
  noSection?: boolean;
  errors?: Record<string, string>;
  fiche?: boolean;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    object: object,
  ) => void;
}

const SituationFamiliale = (props: Props) => {
  const {
    nombreEnfants,
    nombreFreresSoeurs,
    pupille,
    situationFamiliale,
    noSection = false,
    errors = {},
    fiche = false,
    onChange,
  } = props;

  const handlePupilleChange = React.useCallback(
    (event, { name, checked }) => {
      if (!checked) {
        onChange(event, { name, value: 'non' });
      } else {
        onChange(event, { name, value: 'etat' });
      }
    },
    [onChange],
  );

  const handleNumericInput = React.useCallback(
    (event, { name, value }) => {
      const number = value.replace(/[^0-9]/g, '');
      const slicedValue = number.slice(0, 2);
      onChange(event, { name, value: slicedValue });
    },
    [onChange],
  );

  const content = React.useMemo(
    () => (
      <Grid padded>
        <Grid.Row verticalAlign="middle" style={{ padding: '1em' }}>
          <Grid.Column mobile={16} largeScreen={8} widescreen={7}>
            {/** display block car la dropdown ajoute un espace */}
            <Form.Dropdown
              selection
              name="situationFamiliale"
              label="Situation matrimoniale"
              data-testid="situation-familiale-dropdown-situation-matrimoniale"
              options={OPTIONS_SITUATION_MATRIMONIALE}
              value={situationFamiliale}
              onChange={onChange}
              style={{ width: 180, display: 'block' }}
            />
          </Grid.Column>
          <Grid.Column mobile={16} largeScreen={8} widescreen={9}>
            <Form.Group>
              {/* @ts-expect-error style */}
              <Form.Field style={{ display: 'flex', alignItems: 'center' }}>
                <label>&nbsp;</label>
                <Form.Checkbox
                  name="pupille"
                  label="Pupille ?"
                  data-testid="situation-familiale-checkbox-pupille"
                  checked={pupille !== 'non'}
                  onClick={handlePupilleChange}
                  disabled={pupille === 'prefet'}
                />
              </Form.Field>
              {_.map(OPTIONS_PUPILLE, (option) => (
                // @ts-expect-error style
                <Form.Field style={{ display: 'flex', alignItems: 'center' }}>
                  <label>&nbsp;</label>
                  <Form.Radio
                    name="pupille"
                    value={option.value}
                    label={option.text}
                    data-testid={`situation-familiale-radio-${option.value}`}
                    disabled={
                      pupille === 'non' ||
                      _.isNil(pupille) ||
                      pupille === 'prefet'
                    }
                    checked={pupille === option.value}
                    onClick={(event) => {
                      onChange(event, { name: 'pupille', value: option.value });
                    }}
                  />
                </Form.Field>
              ))}
              {fiche && pupille === 'prefet' && (
                // @ts-expect-error style
                <Form.Field style={{ display: 'flex', alignItems: 'center' }}>
                  <label>&nbsp;</label>
                  <Form.Radio
                    name="pupille"
                    value="prefet"
                    label="du préfet"
                    data-testid="situation-familiale-radio-prefet"
                    disabled
                    checked={pupille === 'prefet'}
                  />
                </Form.Field>
              )}
            </Form.Group>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ padding: '1em' }}>
          <Grid.Column mobile={16} largeScreen={8} widescreen={7}>
            <Tooltip
              trigger={
                <Form.Input
                  inline
                  name="nombreFreresSoeurs"
                  type="number"
                  label="Nombre de frères et soeurs vivants"
                  data-testid="situation-familiale-input-freres-soeurs"
                  value={nombreFreresSoeurs}
                  onChange={handleNumericInput}
                  style={{ width: 46 }}
                />
              }
              type={errors?.nombreFreresSoeurs ? 'error' : ''}
              message={errors?.nombreFreresSoeurs}
            />
          </Grid.Column>
          <Grid.Column mobile={16} largeScreen={7} widescreen={9}>
            <Tooltip
              trigger={
                <Form.Input
                  inline
                  name="nombreEnfants"
                  type="number"
                  label="Nombre d'enfants à charge"
                  data-testid="situation-familiale-input-enfants"
                  value={nombreEnfants}
                  onChange={handleNumericInput}
                  style={{ width: 46 }}
                />
              }
              type={errors?.nombreEnfants ? 'error' : ''}
              message={errors?.nombreEnfants}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    ),
    [
      handleNumericInput,
      handlePupilleChange,
      nombreEnfants,
      nombreFreresSoeurs,
      onChange,
      pupille,
      situationFamiliale,
      errors,
      fiche,
    ],
  );

  if (noSection) return content;

  return <Section title="SITUATION FAMILIALE">{content}</Section>;
};

export default SituationFamiliale;
